
"use client";

import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

interface EditNodeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  nodeId: string;
  initialTitle: string;
  initialDescription: string;
  onSave: (title: string, description: string) => Promise<void>;
  onDelete: () => Promise<void>;
  companyId?: string;
}

const EditNodeDialog: React.FC<EditNodeDialogProps> = ({
  isOpen,
  onClose,
  nodeId,
  initialTitle,
  initialDescription,
  onSave,
  onDelete,
  companyId
}) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  
  // Function to extract actual database ID from nodeId
  const extractActualId = (nodeId: string): string => {
    // Handle different node ID formats:
    // kpi-exec-1000, kpi-financial-1001, kpi-initiative-1002, kpi-operating-1003, kpi-123 (regular format)
    console.log("Original nodeId:", nodeId);
    
    if (nodeId.startsWith('kpi-')) {
      // Check if it's the new format with type prefix (kpi-exec-1000, kpi-financial-1001, etc.)
      const matches = nodeId.match(/^kpi-(?:exec|financial|initiative|operating)-(\d+)$/);
      if (matches) {
        console.log("Extracted ID from new format:", matches[1]);
        return matches[1];
      }
      
      // Handle the regular format (kpi-123)
      const regularMatch = nodeId.match(/^kpi-(.+)$/);
      if (regularMatch) {
        console.log("Extracted ID from regular format:", regularMatch[1]);
        return regularMatch[1];
      }
    }
    
    // If no pattern matches, return as-is (fallback)
    console.log("No pattern matched, returning as-is:", nodeId);
    return nodeId;
  };
  
  // Fetch the latest node data from Supabase when the dialog opens
  useEffect(() => {
    const fetchNodeData = async () => {
      if (isOpen && nodeId && companyId) {
        try {
          // Extract actual ID from the nodeId
          const actualId = extractActualId(nodeId);
          
          // Get current user
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) {
            console.error("User not authenticated");
            return;
          }
          
          console.log("Fetching node with actualId:", actualId, "companyId:", companyId, "userId:", user.id);
          
          // Fetch the node data from Supabase - try both numeric and string ID formats
          let data = null;
          let error = null;
          
          // First try treating it as a UUID string
          const { data: uuidData, error: uuidError } = await supabase
            .from('business_value_framework')
            .select('title, description')
            .eq('id', actualId)
            .eq('company_id', companyId)
            .eq('user_id', user.id)
            .maybeSingle();
          
          if (!uuidError && uuidData) {
            data = uuidData;
          } else {
            // If UUID search failed, try by title matching (for generated nodes)
            console.log("UUID search failed, trying title-based search");
            const { data: titleData, error: titleError } = await supabase
              .from('business_value_framework')
              .select('title, description')
              .eq('company_id', companyId)
              .eq('user_id', user.id)
              .eq('title', initialTitle)
              .maybeSingle();
            
            if (!titleError && titleData) {
              data = titleData;
            } else {
              error = titleError || uuidError;
            }
          }
          
          if (error) {
            console.error("Error fetching node data:", error);
            // Fall back to initial values if fetching fails
            setTitle(initialTitle);
            setDescription(initialDescription || "");
          } else if (data) {
            // Update state with the latest data from Supabase
            setTitle(data.title);
            setDescription(data.description || "");
            console.log("Fetched node data from Supabase:", data);
          } else {
            // Fall back to initial values if no data found
            console.log("No data found, using initial values");
            setTitle(initialTitle);
            setDescription(initialDescription || "");
          }
        } catch (error) {
          console.error("Error in fetchNodeData:", error);
          // Fall back to initial values if an exception occurs
          setTitle(initialTitle);
          setDescription(initialDescription || "");
        }
      } else if (isOpen) {
        // If dialog is open but we don't have companyId or nodeId, use initial values
        setTitle(initialTitle);
        setDescription(initialDescription || "");
      }
    };
    
    fetchNodeData();
  }, [isOpen, nodeId, companyId, initialTitle, initialDescription]);
  
  const handleSave = async () => {
    if (!title.trim()) {
      setErrorMessage("Title is required");
      setShowErrorAlert(true);
      return;
    }
    
    try {
      setIsSaving(true);
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !companyId) {
        throw new Error("User not authenticated or company not selected");
      }
      
      console.log("Updating node in Supabase with the following values:");
      console.log("User ID:", user.id);
      console.log("Company ID:", companyId);
      console.log("New Title:", title);
      console.log("New Description:", description);
      
      // Extract actual ID from the nodeId
      const actualId = extractActualId(nodeId);
      console.log("Using actualId for update:", actualId);
      
      // Try to update by ID first
      let updateResult = await supabase
        .from('business_value_framework')
        .update({
          title: title.trim(),
          description: description.trim()
        })
        .eq('id', actualId)
        .eq('user_id', user.id)
        .eq('company_id', companyId);
      
      // If update by ID didn't work (affected 0 rows), try updating by initial title
      if (updateResult.error || updateResult.count === 0) {
        console.log("ID-based update failed or affected 0 rows, trying title-based update");
        updateResult = await supabase
          .from('business_value_framework')
          .update({
            title: title.trim(),
            description: description.trim()
          })
          .eq('title', initialTitle)
          .eq('user_id', user.id)
          .eq('company_id', companyId);
      }
      
      if (updateResult.error) {
        console.error("Error updating node in Supabase:", updateResult.error);
        throw updateResult.error;
      }
      
      console.log("Supabase update response:", updateResult);
      
      // Call the parent onSave function to update the UI
      await onSave(title.trim(), description.trim());
      
      // Show success alert
      setShowSuccessAlert(true);
    } catch (error) {
      console.error("Error updating node:", error);
      setErrorMessage("Failed to save changes. Please try again.");
      setShowErrorAlert(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSuccessAlertClose = () => {
    setShowSuccessAlert(false);
    onClose();
  };

  const handleErrorAlertClose = () => {
    setShowErrorAlert(false);
  };
  
  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => {
        if (!open) onClose();
      }}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit Node</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-start gap-4">
              <label htmlFor="title" className="text-right mt-2">
                Title
              </label>
              <Textarea 
                id="title" 
                value={title} 
                onChange={e => setTitle(e.target.value)} 
                rows={3}
                className="col-span-3 rounded bg-gray-200" 
                placeholder="Enter node title..."
              />
            </div>
            <div className="grid grid-cols-4 items-start gap-4">
              <label htmlFor="description" className="text-right mt-2">
                Description
              </label>
              <Textarea 
                id="description" 
                value={description} 
                onChange={e => setDescription(e.target.value)} 
                rows={6} 
                className="col-span-3 bg-gray-200 rounded"
                placeholder="Enter node description..."
              />
            </div>
          </div>
          <DialogFooter className="flex justify-end">
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} type="button" className="rounded bg-gray-400 hover:bg-gray-300">
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={isSaving} type="button">
                {isSaving ? "Saving..." : "Save"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showSuccessAlert} onOpenChange={setShowSuccessAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Success</AlertDialogTitle>
            <AlertDialogDescription>
              Node updated successfully!
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={handleSuccessAlertClose}>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showErrorAlert} onOpenChange={setShowErrorAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Error</AlertDialogTitle>
            <AlertDialogDescription>
              {errorMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={handleErrorAlertClose}>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default EditNodeDialog;
